import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";
import { SlidersHorizontal, X } from "lucide-react";

const useQuery = () => new URLSearchParams(useLocation().search);

export default function Shop() {
  const { category: paramCat } = useParams();
  const query = useQuery();
  const initialQ = query.get("q") || "";
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState(initialQ);
  useEffect(() => { setQ(initialQ); }, [initialQ]);
  const [brand, setBrand] = useState("");
  const [category, setCategory] = useState(paramCat || "");
  const [sort, setSort] = useState("");
  const [priceMax, setPriceMax] = useState(0);          // 0 = "no cap" until we know actual max
  const [priceCeiling, setPriceCeiling] = useState(0);   // dynamic max from loaded products
  const [brands, setBrands] = useState([]);
  const [cats, setCats] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    api.get("/brands-from-products").then((r) => setBrands(r.data));
    api.get("/categories-from-products").then((r) => setCats(r.data));
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (brand) params.set("brand", brand);
    if (category) params.set("category", category);
    if (sort) params.set("sort", sort);
    api.get(`/products?${params.toString()}`).then((r) => {
      setProducts(r.data);
      if (r.data.length) {
        const max = Math.max(...r.data.map((p) => Number(p.price) || 0), 10000);
        const rounded = Math.ceil(max / 10000) * 10000; // nearest ₹10k for a clean slider
        setPriceCeiling(rounded);
        setPriceMax(rounded); // reset filter so all products show
      } else {
        setPriceCeiling(0);
        setPriceMax(0);
      }
    });
  }, [q, brand, category, sort]);

  const filtered = useMemo(() => products.filter((p) => !priceMax || p.price <= priceMax), [products, priceMax]);

  const clearAll = () => { setQ(""); setBrand(""); setCategory(""); setSort(""); setPriceMax(priceCeiling); };

  return (
    <div className="pt-32 pb-24 container-x" data-testid="shop-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
        <div>
          <div className="kicker mb-2">Catalog</div>
          <h1 className="font-display text-4xl md:text-6xl">Shop <em className="not-italic gold-text">Everything.</em></h1>
        </div>
        <button onClick={() => setShowFilters(!showFilters)} className="btn-ghost-outline lg:hidden text-xs px-5 py-2.5">
          <SlidersHorizontal className="w-4 h-4" /> Filters
        </button>
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-10">
        <aside className={`${showFilters ? "block" : "hidden"} lg:block space-y-6 lg:sticky lg:top-28 lg:h-fit`}>
          <div className="glass rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="kicker">Search</div>
              <button onClick={clearAll} className="text-xs text-white/50 hover:text-gold">Clear</button>
            </div>
            <input type="text" placeholder="Klipsch, Atmos, 4K…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="shop-search" />
          </div>
          <div className="glass rounded-2xl p-6">
            <div className="kicker mb-4">Category</div>
            <div className="space-y-1.5 max-h-64 overflow-y-auto">
              <button onClick={() => setCategory("")} className={`block w-full text-left text-sm py-1.5 px-2 rounded ${!category ? "text-gold" : "text-white/60 hover:text-white"}`}>All</button>
              {cats.map((c) => (
                <button key={c} onClick={() => setCategory(c)} className={`block w-full text-left text-sm py-1.5 px-2 rounded ${category === c ? "text-gold" : "text-white/60 hover:text-white"}`} data-testid={`filter-cat-${c}`}>{c}</button>
              ))}
            </div>
          </div>
          <div className="glass rounded-2xl p-6">
            <div className="kicker mb-4">Brand</div>
            <div className="space-y-1.5 max-h-64 overflow-y-auto">
              <button onClick={() => setBrand("")} className={`block w-full text-left text-sm py-1.5 px-2 rounded ${!brand ? "text-gold" : "text-white/60 hover:text-white"}`}>All</button>
              {brands.map((b) => (
                <button key={b} onClick={() => setBrand(b)} className={`block w-full text-left text-sm py-1.5 px-2 rounded ${brand === b ? "text-gold" : "text-white/60 hover:text-white"}`} data-testid={`filter-brand-${b}`}>{b}</button>
              ))}
            </div>
          </div>
          <div className="glass rounded-2xl p-6">
            <div className="kicker mb-4">Max Price: ₹{(priceMax || priceCeiling || 0).toLocaleString('en-IN')}</div>
            <input type="range" min="1000" max={priceCeiling || 100000} step="1000" value={priceMax || priceCeiling || 0} onChange={(e) => setPriceMax(Number(e.target.value))} className="w-full accent-[#8A6A0F]" data-testid="filter-price" disabled={!priceCeiling} />
            <div className="mt-2 text-[10px] uppercase tracking-[0.2em] text-white/40 flex items-center justify-between">
              <span>₹1k</span>
              <span>₹{((priceCeiling || 0) / 1000).toLocaleString('en-IN')}k</span>
            </div>
          </div>
        </aside>

        <div>
          <div className="flex items-center justify-between mb-6">
            <div className="text-sm text-white/50">{filtered.length} products</div>
            <select value={sort} onChange={(e) => setSort(e.target.value)} className="text-sm !py-2 !pr-10" data-testid="shop-sort">
              <option value="">Sort — Newest first</option>
              <option value="price_asc">Price: Low → High</option>
              <option value="price_desc">Price: High → Low</option>
              <option value="rating">Top Rated</option>
            </select>
          </div>
          {filtered.length === 0 ? (
            <div className="glass rounded-2xl py-24 text-center text-white/50">No products found.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
              {filtered.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
